import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppSettingsService, hd, XSightHttpService } from '@nx-fe/services';
import { TicketParams } from 'app/modules/dashboard/dashboard-page.service';
import { Observable } from 'rxjs';

type IExtendedTicketModel = hd.IExtendedTicketModel;
type ITicketStateParams = hd.ITicketStateParams;
type ITicketParams = hd.ITicketParams;

/** Simple interface describing the parameters to pass for getting ticket statistics */
export interface ITicketStatisticsParams {
  searchText?: string;
  filter?: string;
  propertyGroup?: string;
}

/** Simple interface describing the parameters to pass for counting tickets */
export interface ITicketCountParams {
  searchText?: string;
  filter?: string[];
  stateParams?: ITicketStateParams;
}

export interface ITicketLinkParams {
  ticketId: number;
  searchText?: string;
}

@Injectable()
export class TicketSearchService {
  constructor(
    private appSettingsService: AppSettingsService,
    private $http: XSightHttpService,
    private http: HttpClient
  ) {}

  public getTicketsSearchResource(params: ITicketParams): Observable<hd.IResourceClass<hd.ITicketModel>> {
    return this.$http.get<hd.IResourceClass<hd.ITicketModel>>(this.appSettingsService.ticketServiceUrl, {
      params: params as any,
    });
  }

  public searchTickets = (params: ITicketParams): Observable<hd.ISearchResult<IExtendedTicketModel>> =>
    this.$http.get<hd.ISearchResult<IExtendedTicketModel>>(
      this.appSettingsService.ticketServiceUrl + 'search/' + params.searchText,
      { params: params as any }
    );

  public suggestTickets = (params: ITicketLinkParams): Observable<hd.ISearchResult<IExtendedTicketModel>> =>
    this.$http.get<hd.ISearchResult<IExtendedTicketModel>>(
      `${this.appSettingsService.ticketServiceUrl}search/link/${params.ticketId}/${params.searchText}`
    );

  public getTicketCount = (ticketFilter: ITicketCountParams) =>
    this.$http.get(`${this.appSettingsService.searchServiceUrl}tickets/count`, {
      params: {
        filter: ticketFilter.filter,
        searchText: ticketFilter.searchText || '',
      },
    });

  public getTicketStatistics = (params: ITicketStatisticsParams): Observable<hd.ISearchResult<hd.ISearchStatistic>> => {
    const httpParam = {
      filter: params.filter || '',
      searchText: params.searchText || '',
    };
    return this.$http.get(`${this.appSettingsService.searchServiceUrl}tickets/statistics/${params.propertyGroup}`, {
      params: httpParam,
    });
  };

  public getTicketData(
    chartIds: string[],
    filter: string,
    searchText: string,
    sort: string,
    skip: number,
    take: number,
    group: number,
    assignee: string,
    status: string,
    recency: string,
    tz: number
  ): Observable<any> {
    // TODO:  url is hardcoded, and counts in a fixed order
    // - should return key/value pairs not an array of numbers, since reciever of data is disconnected from request sender
    const filterParams = {
      filter,
      count: 'duetoday,overdue,open,pending,today,this_week,this_month,last_month,this_year,last_year',
      group,
      assignee,
      status,
      recency
    };
    return this.http.post<any>(this.appSettingsService.ticketServiceUrl + 'data', {
      chartIds: chartIds.join(','),
      filterParams: JSON.stringify(filterParams, null, 0),
      searchText,
      sort,
      take,
      skip,
      group,
      tz
    });
  }

  /** @see ISearchService.getCsv */
  public getCsv = (params: TicketParams): Observable<string> => {
    const filterParams = {
      filter: params.filter,
      count: params.status,
      assignee: params.assignee,
      group: params.group,
      status: params.status,
      recency: params.recency
    };

    return this.http.post(
      `${this.appSettingsService.ticketServiceUrl}search/csv`,
      {
        Params: JSON.stringify(filterParams),
        SearchText: params.searchText || '',
        Sort: params.order,
        TZ: params.timeZoneOffset
      },
      {
        headers: { Accept: 'text/csv;UTF-8' },
        responseType: 'text',
      }
    );
  };
}
